import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body2,
    Box,
    Flex,
    HorizontalDivider,
    IconChevronRight,
    OutlinedCard,
    Stack,
    Subtitle1,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useActivityConfig } from '@phx/myphx-lib';

import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import { getFragment } from '../../../graphql';
import { getLocationInfoFragment } from '../../../graphql/client/helpers/get-location-fragment';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    type PriceQuoteInfoFragment,
    RenewalStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import {
    formatToLocalizedDate,
    getAbsoluteRoute,
    titleCase,
} from '../../../util';

export type LastOrderActivityProps = {
    priceQuote?: PriceQuoteInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const LastOrderActivity = ({
    priceQuote,
    prescriberOrder,
}: LastOrderActivityProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enableRxCancelMyphx, enableRxRenewalMyphx } = useFlags();

    const pharmacy = priceQuote
        ? getLocationInfoFragment(priceQuote?.location)
        : undefined;
    const pharmacyName = getFragment(pharmacy)?.name;

    const navigateToOrderConfirmation = useCallback(() => {
        navigate(
            getAbsoluteRoute('orders.prescriberOrderId.confirmation', {
                prescriberOrderId: prescriberOrder.id,
            })
        );
    }, [navigate, prescriberOrder.id]);

    const navigateToRenewalStatus = useCallback(() => {
        navigate(
            getAbsoluteRoute('orders.prescriberOrderId.renewalStatus', {
                prescriberOrderId: prescriberOrder.id,
            })
        );
    }, [navigate, prescriberOrder.id]);

    // Temporary until VELA integration
    const isRouted = undefined;

    const activityConfigs = useActivityConfig({
        prescriberOrder,
        navigateToRenewalStatus,
        navigateToOrderConfirmation,
        pharmacyName,
        enableRxCancel: enableRxCancelMyphx,
        enableRxRenewal: enableRxRenewalMyphx,
        titleCase,
        RenewalStatus,
        PrescriberOrderStatus,
        isRouted,
    });

    return (
        <ConditionalComponent
            condition={
                prescriberOrder.status === PrescriberOrderStatus.Cancelled ||
                !!prescriberOrder.latestSelectedOffer ||
                !!prescriberOrder.latestSelectedPriceQuote
            }
        >
            <OutlinedCard>
                <Stack gap="xs">
                    <Subtitle1>{t('orderDetails.lastActivity')}</Subtitle1>
                    <HorizontalDivider />
                    <Stack gap="xs">
                        {activityConfigs.map((activityConfig, index) => (
                            <Stack key={index} gap="xxxs">
                                <Flex gap="xs" justify="space-between">
                                    <Subtitle2>
                                        {activityConfig.label}
                                    </Subtitle2>
                                    {activityConfig.timestamp && (
                                        <Body2>
                                            {formatToLocalizedDate(
                                                new Date(
                                                    activityConfig.timestamp
                                                ),
                                                {
                                                    dateStyle: 'medium',
                                                    timeZone:
                                                        activityConfig.timeZone,
                                                },
                                                i18n.language
                                            )}
                                        </Body2>
                                    )}
                                </Flex>
                                {activityConfig.additionalContent}
                                {activityConfig.action && (
                                    <Box>
                                        <TextButton
                                            RightIcon={IconChevronRight}
                                            onClick={
                                                activityConfig.action.onClick
                                            }
                                        >
                                            {activityConfig.action.label}
                                        </TextButton>
                                    </Box>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </OutlinedCard>
        </ConditionalComponent>
    );
};
